// Brand-kit styling for the brand's own site (blog Article renderer). Kit fonts are registered by
// kit-fonts.ts under scoped aliases (`hc-kit-heading`, ...), so a kit font named like an app font
// ("Inter") never restyles the HelloConvo UI around the preview.
import type { FontRole, PaletteRole } from "../../../../shared/visuals";
import type { PreviewIdentity } from "../../../../shared/content-preview";

/** Neutral site defaults used when a brand has no kit (or a role is unset). */
export const NEUTRAL_PALETTE: Record<PaletteRole, string> = {
  background: "#FFFFFF",
  surface: "#F8FAFC",
  text: "#0F172A",
  muted: "#64748B",
  accent: "#2563EB",
  highlight: "#7C3AED",
};

const FALLBACK_STACKS: Record<FontRole, string> = {
  heading: "Georgia, 'Times New Roman', serif",
  body: "ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif",
  mono: "ui-monospace, SFMono-Regular, Menlo, monospace",
};

const HEX = /^#[0-9a-f]{6}$/i;

/** A family name that is safe inside a quoted CSS string (letters, digits, space, - and _). */
export function safeFamily(name: string | null | undefined): string | null {
  const cleaned = (name ?? "").replace(/[^A-Za-z0-9 _-]/g, "").replace(/\s+/g, " ").trim().slice(0, 80);
  return cleaned || null;
}

export function kitFontAlias(role: FontRole): string {
  return `hc-kit-${role}`;
}

/** CSS font-family stack for a role: loaded kit file, then the declared family, then neutral fallbacks. */
export function fontStack(identity: Pick<PreviewIdentity, "fonts" | "font_files">, role: FontRole): string {
  const parts: string[] = [];
  if (identity.font_files.some((file) => file.role === role)) parts.push(`"${kitFontAlias(role)}"`);
  const declared = safeFamily(identity.fonts[role]) ?? safeFamily(identity.font_files.find((file) => file.role === role)?.family);
  if (declared) parts.push(`"${declared}"`);
  parts.push(FALLBACK_STACKS[role]);
  return parts.join(", ");
}

/** Palette with neutral defaults for missing or malformed roles. */
export function sitePalette(identity: Pick<PreviewIdentity, "palette">): Record<PaletteRole, string> {
  const out = { ...NEUTRAL_PALETTE };
  for (const role of Object.keys(NEUTRAL_PALETTE) as PaletteRole[]) {
    const value = identity.palette[role];
    if (value && HEX.test(value)) out[role] = value;
  }
  return out;
}
